import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { DialogPromptListenSpeak } from '../../api/DialogPromptApi';
import AudioWaveIcon from '../../asset/icons8-audio-wave.gif';
import ChatBubble from '../ChatBubble';
import WordPinyin from '../WordPinyin';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // outline rounded doesn't exist?
import AddSelfStudyBlueIcon from '../../asset/icon-add-self-study-blue.png';
import MicrophoneBlueIcon from '../../asset/icon-microphone-blue.png';
import RightArrowBlueIcon from '../../asset/icon-right-arrow-blue.png';

import { useHistory, useLocation } from 'react-router-dom';
import { ScoreGrade, hasAnySpokenSentenceScore, sentenceScoreAllGood, sentenceScoreNonSilentFailed, sentenceScoreAnyFailed } from '../../Score';
import { AudioApi } from '../../api/AudioApi';
import { ISentenceScore, ScoreApi } from '../../api/ScoreApi';
import AppBarContext from '../../context/AppBarContext';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import AddSentenceDialog from '../AddSentenceDialog';
import { handleErrorMessage } from '../ErrorDialog';
import { TeacherAudioActions, getSelectedTeacherSpeakerRole } from '../TeacherAudioReportButton';
import { SentenceScoreMediumResult, SentenceScoreTryAgain, SentenceScoreTryAgainNo, SentenceScoreTryAgainYes, getAudioByText } from './DialogPromptComponent';

import Popover from '@mui/material/Popover';
import { useAudioRecorder } from '../../AudioRecorder';
import { UserDialogPromptApi } from '../../api/UserDialogPromptApi';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';

interface DialogPromptListenSpeakComponentProps {
    dialogPrompt: DialogPromptListenSpeak;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

const DialogPromptListenSpeakComponent: React.FC<DialogPromptListenSpeakComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();

    const { setLeftNavigationList, setRightNavigationList, setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [isScoring, setIsScoring] = React.useState(false);

    const [sentenceScoreList, setSentenceScoreList] = React.useState<ISentenceScore[]>([]);

    const [chatBubbleSentenceScoreDone, setChatBubbleSentenceScoreDone] = React.useState<boolean[]>([]);
    const [chatBubbleTeacherResponseDone, setChatBubbleTeacherResponseDone] = React.useState<boolean[]>([]);
    const [tryAgainClickedList, setTryAgainClickedList] = React.useState<(boolean | null)[]>([]);

    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);
    const [chatBubbleTryAgainDone, setChatBubbleTryAgainDone] = React.useState(false);
    const [chatBubbleContinueDone, setChatBubbleContinueDone] = React.useState(false);

    const storedLearnWithCharacter = localStorage.getItem('learnWithCharacter');
    const learnWithCharacter = storedLearnWithCharacter ? JSON.parse(storedLearnWithCharacter) : false;

    const wordPinyinRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        if (!chatBubbleDone || !wordPinyinRef.current) return;
        const observer = new MutationObserver(() => {
            if (wordPinyinRef.current) {
                scrollElementIntoViewWithFallback(wordPinyinRef.current);
                observer.disconnect(); // only do it once
            }
        });
        observer.observe(wordPinyinRef.current, {
            childList: true,
            subtree: true,
        });
        return () => observer.disconnect(); // clean up if component unmounts
    }, [chatBubbleDone]);

    const handleSpeakerClick = () => {
        const audioPromise = AudioApi.getTeacherSentence(dialogPrompt.sentence_id)

        audioPromise
            .then(response => {
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Getting male audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(blob => {
                playAudio(blob)
                // create a new Audio object and set the source to the blob URL
                // const audio = new Audio(URL.createObjectURL(blob));
                // audio.play();
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    };

    const onStop = React.useCallback((audioData: Blob) => {
        setIsScoring(true)
        const formData = new FormData();
        formData.append('audio', audioData);

        ScoreApi.getScore(dialogPrompt.sentence_id, formData)
            .then(response => {
                setIsScoring(false)
                if (response.status >= 300) {
                    throw new Error('Scoring audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(responseJson => {
                UserDialogPromptApi.addSpeakingAttempt(dialogPrompt.id, responseJson.score_mean)
                    .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
                responseJson.audioData = audioData;
                setSentenceScoreList((previous) => [...previous, responseJson]);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [dialogPrompt.sentence_id, dialogPrompt.id, setErrorMessage, history, location]);
    const { startRecording, stopRecording, isRecording } = useAudioRecorder({ setErrorMessage, onStop });

    const [infoAnchorEl, setInfoAnchorEl] = React.useState<null | HTMLElement>(null);
    const infoOpen = Boolean(infoAnchorEl);

    const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
        setInfoAnchorEl(event.currentTarget);
    };

    const handleInfoClose = () => {
        setInfoAnchorEl(null);
    };

    const wordAudioReportContext = React.useMemo(() => ({
        sentenceId: dialogPrompt.sentence_id,
        dialogPromptId: dialogPrompt.id,
        textToSpeechSpeakerId: getSelectedTeacherSpeakerRole(),
        audioText: dialogPrompt.new_word,
        audioRole: 'listen_speak_word_card',
    }), [dialogPrompt.id, dialogPrompt.new_word, dialogPrompt.sentence_id]);


    const [addSentenceDialogOpen, setAddSentenceDialogOpen] = React.useState(false);
    const [, setAddSentenceSuccessMessage] = React.useState('');

    const handleAddSentenceClick = () => {
        setAddSentenceDialogOpen(true);
    };


    const handleClose = () => {
        setAddSentenceDialogOpen(false);
        setAddSentenceSuccessMessage('')
    };

    const [shouldJiggleSkipArrow, setShouldJiggleSkipArrow] = React.useState(false);
    const jiggleKeyframes = React.useMemo(() => ({
        '@keyframes jiggle': {
            '0%': { transform: 'rotate(0deg)' },
            '12.5%': { transform: 'rotate(-20deg)' },
            '37.5%': { transform: 'rotate(20deg)' },
            '52.5%': { transform: 'rotate(-20deg)' },
            '77.5%': { transform: 'rotate(20deg)' },
            '100%': { transform: 'rotate(0)' },
        },
    }), []);
    const jiggleDuration = 500
    const jiggleIteration = 8
    React.useEffect(() => {
        if (sentenceScoreList.length === 0) return;

        const last = sentenceScoreList[sentenceScoreList.length - 1];
        if (!sentenceScoreNonSilentFailed(last)) {
            setShouldJiggleSkipArrow(false);
            return;
        }

        const failedCount = sentenceScoreList.reduce(
            (acc, sentenceScore) => acc + (sentenceScoreNonSilentFailed(sentenceScore) ? 1 : 0),
            0
        );

        if (failedCount % 2 === 0) {
            setShouldJiggleSkipArrow(true);
            const totalDuration = jiggleDuration * jiggleIteration
            const t = setTimeout(() => setShouldJiggleSkipArrow(false), totalDuration + 50); // length of the animation
            return () => clearTimeout(t);
        } else {
            setShouldJiggleSkipArrow(false);
        }
    }, [sentenceScoreList]);

    React.useEffect(() => {
        if (chatBubbleDone && chatBubbleTryAgainDone == false) {
            if (sentenceScoreList.length == 0 || sentenceScoreList[sentenceScoreList.length - 1].passing_grade == ScoreGrade.Bad || tryAgainClickedList[sentenceScoreList.length - 1]) {
                if (hasAnySpokenSentenceScore(sentenceScoreList)) {
                    setLeftNavigationList([
                        <IconButton
                            aria-label="Skip exercise"
                            onClick={handleCancelClick}
                            sx={{
                                ...jiggleKeyframes,
                                animation: shouldJiggleSkipArrow ? `jiggle ${jiggleDuration}ms ease-in-out ${jiggleIteration}` : 'none',
                            }}
                        >
                            <img src={RightArrowBlueIcon} alt="" style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                        </IconButton>
                    ])
                }
                setRightNavigationList([
                    <Tooltip title="Add to Pronunciation" arrow>
                        <IconButton onClick={handleAddSentenceClick} sx={{ alignSelf: 'center' }}>
                            <img src={AddSelfStudyBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                        </IconButton>
                    </Tooltip>
                ])
                setActionList([
                    <IconButton onClick={isRecording ? stopRecording : startRecording}>
                        {isRecording ? <img src={AudioWaveIcon} alt="Recording" style={{ height: '100%', width: 'auto', maxHeight: '36px' }} />
                            : isScoring ? <CircularProgress size={24} sx={{ height: '100%', width: 'auto', maxHeight: '36px' }} />
                                : <img src={MicrophoneBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '36px' }} />
                        }
                    </IconButton>
                ])
            }
        }
    }, [sentenceScoreList, isRecording, isScoring, chatBubbleDone, chatBubbleTryAgainDone, shouldJiggleSkipArrow, jiggleKeyframes, setActionList, setLeftNavigationList, setRightNavigationList, startRecording, stopRecording, tryAgainClickedList]);

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [yesResponseAudio, setYesResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [noResponseAudio, setNoResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [sentenceScoreMediumResultAudio, setSentenceScoreMediumResultAudio] = React.useState<Blob | null | undefined>(undefined);
    const [sentenceScoreTryAgainAudio, setSentenceScoreTryAgainAudio] = React.useState<Blob | null | undefined>(undefined);
    const [, setSentenceScoreTryAgainYesAudio] = React.useState<Blob | null | undefined>(undefined);
    const [, setSentenceScoreTryAgainNoAudio] = React.useState<Blob | null | undefined>(undefined);

    React.useEffect(() => {
        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.yes_response, setYesResponseAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.no_response, setNoResponseAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreMediumResult, setSentenceScoreMediumResultAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgain, setSentenceScoreTryAgainAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgainYes, setSentenceScoreTryAgainYesAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgainNo, setSentenceScoreTryAgainNoAudio)
    }, [clearFooterList, dialogPrompt.no_response, dialogPrompt.prompt, dialogPrompt.yes_response, history, setErrorMessage]);

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
        }
    }, [sentenceScoreList, chatBubbleContainerRef]);

    const updateTryAgainClickedList = React.useCallback((tryAgain: boolean) => {
        const index = sentenceScoreList.length - 1;
        setTryAgainClickedList(previous => {
            const updatedArray = [...previous];
            updatedArray[index] = tryAgain;
            return updatedArray;
        })
    }, [sentenceScoreList.length, setTryAgainClickedList]);

    const handleNextWordClick = React.useCallback(() => {
        setChatBubbleTryAgainDone(false)
        updateTryAgainClickedList(false)
    }, [setChatBubbleTryAgainDone, updateTryAgainClickedList]);
    const handleTryAgainClick = React.useCallback(() => {
        setChatBubbleTryAgainDone(false)
        updateTryAgainClickedList(true)
    }, [setChatBubbleTryAgainDone, updateTryAgainClickedList]);

    React.useEffect(() => {
        if (chatBubbleTryAgainDone) {
            setLeftNavigationList([
                <IconButton aria-label="Skip exercise" onClick={handleCancelClick}>
                    <img src={RightArrowBlueIcon} alt="" style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                </IconButton>
            ])
            setActionList([
                <Button variant="contained" onClick={handleNextWordClick} className="secondaryButton">
                    No, move on
                </Button>,
                <Button variant="contained" onClick={handleTryAgainClick} >
                    Yes, try again
                </Button>
            ])
        }
    }, [chatBubbleTryAgainDone, handleNextWordClick, handleTryAgainClick, setActionList, setLeftNavigationList]);

    const [openCancelDialog, setOpenCancelDialog] = React.useState(false);

    const handleCancelClick = () => {
        setOpenCancelDialog(true);
    };

    const handleCancelDialogConfirm = () => {
        setOpenCancelDialog(false);
        setChatBubbleContinueDone(true);
    };

    const handleCancelDialogCancel = () => {
        setOpenCancelDialog(false);
    };

    React.useEffect(() => {
        if (chatBubbleContinueDone) {
            setDialogPromptComplete(dialogPrompt.id)

        }
    }, [chatBubbleContinueDone, dialogPrompt.id, setDialogPromptComplete])

    return (
        <Stack>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                chatBubbleContainerRef={chatBubbleContainerRef}
                setChatBubbleDone={setChatBubbleDone}
                dialogPromptId={dialogPrompt.id}
                audioRole="prompt"
            />

            {chatBubbleDone && (
                <Box className="border-dark"
                    sx={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',  // Ensures inner content is centered
                        width: 'fit-content',  // Keeps width only as large as needed
                        margin: '0 auto',      // Centers the Box horizontally
                        pb: 1, mb: 2,
                        maxWidth: '90%'
                    }}
                >
                    <Stack ref={wordPinyinRef} direction="row" alignItems="center" justifyContent="center" flexWrap="wrap" sx={{ pl: 2, pr: 3, display: 'inline-flex' }}>
                        <TeacherAudioActions context={wordAudioReportContext} onPlay={handleSpeakerClick} />
                        <WordPinyin
                            text={dialogPrompt.new_word}
                            pinyinList={dialogPrompt.new_word_pinyin}
                            showCharacter={learnWithCharacter}
                        />
                        {dialogPrompt.new_word_info_text && (<>
                            <IconButton onClick={handleInfoClick}>
                                <InfoOutlinedIcon sx={{
                                    width: 30,          // bigger width
                                    height: 30,         // bigger height
                                    color: '#0062FF'
                                }} />
                            </IconButton>
                            <Popover
                                open={infoOpen}
                                anchorEl={infoAnchorEl}
                                onClose={handleInfoClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                            >
                                <Box sx={{ p: 2, maxWidth: '300px' }}>
                                    <Typography variant="body2">{dialogPrompt.new_word_info_text}</Typography>
                                </Box>
                            </Popover>
                        </>)}
                    </Stack>
                </Box>
            )
            }

            {
                sentenceScoreList.map((sentenceScore, index) => (
                    <div key={index}>
                        <ChatBubble
                            isTeacher={!dialogPrompt.is_teacher}
                            message={sentenceScore.text}
                            messageAudio={null}
                            sentenceScore={sentenceScore}
                            showAvatar={showAvatar}
                            chatBubbleContainerRef={chatBubbleContainerRef}
                            setChatBubbleDone={(done) => setChatBubbleSentenceScoreDone(previous => {
                                if (previous[index] === done) return previous;
                                const updatedArray = [...previous];
                                updatedArray[index] = done;
                                return updatedArray;
                            })}
                        />
                        {chatBubbleSentenceScoreDone[index] && (<>
                            {sentenceScoreAllGood(sentenceScore) ? (
                                <ChatBubble
                                    isTeacher={dialogPrompt.is_teacher}
                                    message={dialogPrompt.yes_response}
                                    messageAudio={yesResponseAudio ? yesResponseAudio : null}
                                    showAvatar={showAvatar}
                                    chatBubbleContainerRef={chatBubbleContainerRef}
                                    dialogPromptId={dialogPrompt.id}
                                    audioRole="yes_response"
                                    setChatBubbleDone={setChatBubbleContinueDone}
                                />
                            ) : sentenceScoreAnyFailed(sentenceScore) ? (
                                <ChatBubble isTeacher={dialogPrompt.is_teacher}
                                    message={dialogPrompt.no_response}
                                    messageAudio={noResponseAudio ? noResponseAudio : null}
                                    showAvatar={showAvatar}
                                    chatBubbleContainerRef={chatBubbleContainerRef}
                                    dialogPromptId={dialogPrompt.id}
                                    audioRole="no_response"
                                    setChatBubbleDone={() => {
                                        setChatBubbleTryAgainDone(false);
                                        // set null, to skip this index
                                        setTryAgainClickedList((previous) => [...previous, null]);
                                    }}
                                />
                            ) : (
                                <ChatBubble isTeacher={dialogPrompt.is_teacher}
                                    message={SentenceScoreMediumResult}
                                    messageAudio={sentenceScoreMediumResultAudio ? sentenceScoreMediumResultAudio : null}
                                    showAvatar={showAvatar}
                                    chatBubbleContainerRef={chatBubbleContainerRef}
                                    dialogPromptId={dialogPrompt.id}
                                    audioRole="sentence_score_medium_result"
                                    setChatBubbleDone={(done) => setChatBubbleTeacherResponseDone(previous => {
                                        if (previous[index] === done) return previous;
                                        const updatedArray = [...previous];
                                        updatedArray[index] = done;
                                        return updatedArray;
                                    })}
                                />
                            )}
                            {chatBubbleTeacherResponseDone[index] && (
                                <ChatBubble
                                    isTeacher={true}
                                    message={SentenceScoreTryAgain}
                                    messageAudio={sentenceScoreTryAgainAudio ? sentenceScoreTryAgainAudio : null}
                                    showAvatar={showAvatar}
                                    chatBubbleContainerRef={chatBubbleContainerRef}
                                    dialogPromptId={dialogPrompt.id}
                                    audioRole="sentence_score_try_again"
                                    setChatBubbleDone={setChatBubbleTryAgainDone}
                                />
                            )}
                            {tryAgainClickedList[index] === true && (
                                <ChatBubble
                                    isTeacher={false}
                                    message={SentenceScoreTryAgainYes}
                                    messageAudio={null} // no audio for student
                                    showAvatar={showAvatar}
                                    chatBubbleContainerRef={chatBubbleContainerRef}
                                />
                            )}
                            {tryAgainClickedList[index] === false && (
                                <ChatBubble
                                    isTeacher={false}
                                    message={SentenceScoreTryAgainNo}
                                    messageAudio={null} // no audio for student
                                    showAvatar={showAvatar}
                                    chatBubbleContainerRef={chatBubbleContainerRef}
                                    setChatBubbleDone={setChatBubbleContinueDone}
                                />

                            )}
                        </>)}
                    </div>
                ))
            }
            {/* Skip Confirmation Dialog */}
            <Dialog open={openCancelDialog} onClose={handleCancelDialogCancel}>
                <DialogTitle><Typography variant="h4" sx={{ color: '#383838' }}>Confirm Skip</Typography></DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#8D8D8D' }}>
                        Are you sure you want to skip this exercise?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', '& .MuiButton-root': { flex: 1 } }}>
                    <Button onClick={handleCancelDialogCancel} color="primary" sx={{ color: '#787878' }}>
                        Keep trying
                    </Button>
                    <Button onClick={handleCancelDialogConfirm} color="primary" sx={{ color: '#787878' }}>
                        Skip
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Add Sentence Confirmation Dialog */}
            <AddSentenceDialog
                open={addSentenceDialogOpen}
                sentence={{ text: dialogPrompt.new_word, id: dialogPrompt.sentence_id }}
                onClose={handleClose}
            />
        </Stack >
    );
};

export default DialogPromptListenSpeakComponent;
